'use server'

import { revalidatePath } from 'next/cache'
import { createClient } from '@/lib/supabase/server'
import { effectiveTier } from '@/lib/ai-usage'
import { isOwnerEmail } from '@/lib/owner'
import { BUILD_MAX, DEVICE_MAX, FEEDBACK_REWARD_AKCE, NOTE_MAX, clip, isFeedbackKind, isFeedbackReward, isFeedbackStatus, normalizeFeedbackText, pagePath, parseRewardAkce } from '@/lib/feedback'
import { createAppFeedback, rewardFeedback, updateFeedback } from '@/lib/feedback-server'

// Обращения учеников (.scratch/autopilot/feedback.md): отправка из формы
// (feedback-form.tsx) и смена статуса владельцем (/founder/feedback).

export type FeedbackResult = { ok: true } | { ok: false; error: string }

export type FeedbackInput = { kind: string; text: string; page?: string; device?: string; build?: string }

export async function sendFeedback(input: FeedbackInput): Promise<FeedbackResult> {
  const supabase = await createClient()
  const { data: claimsData } = await supabase.auth.getClaims()
  const userId = claimsData?.claims?.sub as string | undefined
  if (!userId) return { ok: false, error: 'Чтобы написать нам, войдите в аккаунт. Без входа можно написать на support@turkido.ru.' }
  if (!isFeedbackKind(input.kind)) return { ok: false, error: 'Выберите, что это: пожелание или ошибка.' }
  const norm = normalizeFeedbackText(input.text)
  if ('error' in norm) return { ok: false, error: norm.error }

  const { data: profile } = await supabase
    .from('profiles')
    .select('subscription_status, subscription_until, plus_until, pro_until')
    .eq('id', userId)
    .maybeSingle()

  const res = await createAppFeedback({
    userId,
    kind: input.kind,
    text: norm.text,
    tier: effectiveTier(profile),
    page: pagePath(input.page),
    device: clip(input.device, DEVICE_MAX),
    build: clip(input.build, BUILD_MAX),
  })
  return res.ok ? { ok: true } : res
}

async function isOwner(): Promise<boolean> {
  const supabase = await createClient()
  const { data } = await supabase.auth.getClaims()
  return isOwnerEmail(data?.claims?.email)
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Форма на /founder/feedback: id, status и note. Проверка владельца здесь, а
// не только на странице: server action можно вызвать и мимо неё.
export async function setFeedbackStatus(form: FormData): Promise<void> {
  if (!(await isOwner())) return
  const id = form.get('id')
  const status = form.get('status')
  if (typeof id !== 'string' || !UUID.test(id) || !isFeedbackStatus(status)) return
  const noteRaw = form.get('note')
  const note = typeof noteRaw === 'string' ? noteRaw.trim().slice(0, NOTE_MAX) || null : null
  const res = await updateFeedback(id, status, note)
  if (!res.ok) console.error('[feedback] статус не изменён:', res.error)
  revalidatePath('/founder/feedback')
  revalidatePath('/founder')
}

// Кнопки "Принять: N akçe" (сумму владелец вводит, 29.10) и "Принять: месяц
// Plus" на /founder/feedback (тикет 96). Проверка владельца здесь же; остальное в lib/feedback-server.
export async function rewardFeedbackAction(form: FormData): Promise<void> {
  if (!(await isOwner())) return
  const id = form.get('id')
  const reward = form.get('reward')
  if (typeof id !== 'string' || !UUID.test(id) || !isFeedbackReward(reward)) return
  const akce = reward === 'akce' ? parseRewardAkce(form.get('akce')) : FEEDBACK_REWARD_AKCE
  if (akce === null) return
  const res = await rewardFeedback(id, reward, akce)
  if (!res.ok) console.error('[feedback] награда не выдана:', res.error)
  revalidatePath('/founder/feedback')
  revalidatePath('/founder')
}
